/**
 * Pure helpers for the pack file tree: build nested folders from flat paths,
 * compact single-child folder chains (PyCharm "compact middle packages") and
 * flatten the visible nodes for rendering and keyboard navigation.
 */
import type { PackFileLike } from "./packFiles";

export interface TreeFileNode<T extends PackFileLike> {
  kind: "file";
  /** The file path. */
  id: string;
  name: string;
  file: T;
}

export interface TreeFolderNode<T extends PackFileLike> {
  kind: "folder";
  /** Full path of the deepest folder of a compacted chain. */
  id: string;
  /** Display name; a compacted chain reads "dynamic_evals/anxiety". */
  name: string;
  /** True when any file below is edited. */
  edited: boolean;
  children: TreeNode<T>[];
}

export type TreeNode<T extends PackFileLike> =
  TreeFileNode<T> | TreeFolderNode<T>;

interface RawFolder<T extends PackFileLike> {
  name: string;
  id: string;
  folders: Map<string, RawFolder<T>>;
  files: T[];
}

function makeFolder<T extends PackFileLike>(
  name: string,
  id: string,
): RawFolder<T> {
  return { name, id, folders: new Map(), files: [] };
}

function hasEdited<T extends PackFileLike>(node: TreeNode<T>): boolean {
  return node.kind === "file" ? node.file.edited : node.edited;
}

function byName(a: { name: string }, b: { name: string }): number {
  return a.name.localeCompare(b.name);
}

function convert<T extends PackFileLike>(raw: RawFolder<T>): TreeFolderNode<T> {
  let current = raw;
  let name = raw.name;
  // Compact while the folder holds exactly one folder and no files.
  while (current.files.length === 0 && current.folders.size === 1) {
    const [only] = [...current.folders.values()];
    if (!only) break;
    current = only;
    name = `${name}/${current.name}`;
  }
  const folders = [...current.folders.values()].map(convert).sort(byName);
  const files: TreeFileNode<T>[] = current.files
    .map((file) => ({
      kind: "file" as const,
      id: file.path,
      name: file.path.slice(file.path.lastIndexOf("/") + 1),
      file,
    }))
    .sort(byName);
  const children: TreeNode<T>[] = [...folders, ...files];
  return {
    kind: "folder",
    id: current.id,
    name,
    edited: children.some(hasEdited),
    children,
  };
}

/** Nested, compacted tree: folders first, then files, each sorted by name. */
export function buildFileTree<T extends PackFileLike>(
  files: readonly T[],
): TreeNode<T>[] {
  const root = makeFolder<T>("", "");
  for (const file of files) {
    const parts = file.path.split("/");
    parts.pop();
    let folder = root;
    parts.forEach((part, index) => {
      let next = folder.folders.get(part);
      if (!next) {
        next = makeFolder<T>(part, parts.slice(0, index + 1).join("/"));
        folder.folders.set(part, next);
      }
      folder = next;
    });
    folder.files.push(file);
  }
  const folders = [...root.folders.values()].map(convert).sort(byName);
  const rootFiles: TreeFileNode<T>[] = root.files
    .map((file) => ({
      kind: "file" as const,
      id: file.path,
      name: file.path,
      file,
    }))
    .sort(byName);
  return [...folders, ...rootFiles];
}

/** A folder is open when the user said so, else when it is searched or holds the selected file. */
export function isFolderOpen(
  folderId: string,
  options: {
    searching: boolean;
    selected: string | null;
    overrides: ReadonlyMap<string, boolean>;
  },
): boolean {
  const override = options.overrides.get(folderId);
  if (override !== undefined) return override;
  if (options.searching) return true;
  return options.selected?.startsWith(`${folderId}/`) ?? false;
}

export interface VisibleNode<T extends PackFileLike> {
  node: TreeNode<T>;
  level: number;
  parentId: string | null;
  open: boolean;
}

/** Depth-first list of the nodes that are currently shown. */
export function flattenVisible<T extends PackFileLike>(
  nodes: readonly TreeNode<T>[],
  isOpen: (folderId: string) => boolean,
  level = 1,
  parentId: string | null = null,
): VisibleNode<T>[] {
  return nodes.flatMap((node) => {
    const open = node.kind === "folder" && isOpen(node.id);
    const self: VisibleNode<T> = { node, level, parentId, open };
    return node.kind === "folder" && open
      ? [self, ...flattenVisible(node.children, isOpen, level + 1, node.id)]
      : [self];
  });
}

/** Language code for a folder whose name has a `he` or `en` segment. */
export function languageOf(folderName: string): "he" | "en" | null {
  const hit = folderName.split("/").find((s) => s === "he" || s === "en");
  return hit === "he" || hit === "en" ? hit : null;
}
